import { AdsTargetingProvider } from '@folklore/ads';
import { useWindowSize } from '@folklore/hooks';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { useEffect, useState, ReactNode, Ref } from 'react';

import useElementSize from '../../hooks/useElementSize';
import useScrollEnter from '../../hooks/useScrollEnter';
import { usePageViewTracking } from '../../hooks/useTracking';
import useUniqueDocumentsBlocks, {
    BlockWithUniqueDocuments,
} from '../../hooks/useUniqueDocumentsBlocks';
import {
    createBrandNewRow,
    createBrandPopularRow,
    createBrandSeriesRow,
    createBrandVideosRow,
    createDocumentsGrid,
} from '../../lib/blocks';
import getBlocksWithAddons from '../../lib/getBlocksWithAddons';

import HeaderAd from '../ads/HeaderAd';
import Blocks from '../blocks/Blocks';
import BrandHeader from '../headers/BrandHeader';
import Bump from '../partials/Bump';
import RowTitle from '../partials/RowTitle';

import styles from '../../styles/pages/home-page-blocks.module.css';

export interface HomePageBlocksProps {
    brand?: Brand | null;
    header?: ReactNode;
    blocks?: Block[] | null;
    cardTheme?: CardTheme | LegacyCardTheme | null;
    theme?: string | null;
    pageType?: string;
    query?: Record<string, unknown> | null;
    additionalBlocks?: Block[] | null;
    adInterval?: number;
    current?: boolean;
    withBorder?: boolean;
    withoutBumper?: boolean;
    withoutHeaderSections?: boolean;
    withoutFeatured?: boolean;
    withoutNew?: boolean;
    withoutPopular?: boolean;
    withoutVideos?: boolean;
    withoutSeries?: boolean;
    withoutSections?: boolean;
    className?: string | null;
    contentClassName?: string | null;
    blocksClassName?: string | null;
    onEnter?: (() => void) | null;
    onLeave?: (() => void) | null;
}

const HomePageBlocks = ({
    brand = null,
    header = null,
    blocks = null,
    theme = null,
    pageType = 'home',
    query = null,
    cardTheme = 'box-translucent',
    additionalBlocks = null,
    adInterval = 1,
    current = false,
    withBorder = false,
    withoutBumper = false,
    withoutHeaderSections = false,
    withoutFeatured = false,
    withoutNew = false,
    withoutPopular = false,
    withoutVideos = false,
    withoutSeries = false,
    withoutSections = false,
    className = null,
    contentClassName = null,
    blocksClassName = null,
    onEnter = null,
    onLeave = null,
}: HomePageBlocksProps) => {
    const url = useUrlGenerator();
    const {
        id: brandId = null,
        handle: brandHandle = null,
        slug: brandSlug = null,
        sections = null,
    } = brand || {};

    const finalBlocks =
        blocks ||
        [
            !withoutFeatured
                ? {
                      role: 'block',
                      type: 'header_featured_list',
                      brand,
                      withBrandLogo: false,
                      cardTheme: 'over-bottom-left',
                      cardWithout: ['category'],
                      withBorderRadius: true,
                      className: styles.featuredHeader,
                      count: 1,
                      query: {
                          brand: brandId,
                          exclude_type: ['contest', 'newsletter'],
                          placement: [`home_featured_${brandHandle}`],
                          ...query,
                      },
                      uniqueDocuments: true,
                  }
                : null,
            !withoutNew
                ? createBrandNewRow(brand, {
                      urlGenerator: url,
                      cardTheme,
                      withBorder,
                      query,
                  })
                : null,
            !withoutPopular
                ? createBrandPopularRow(brand, {
                      urlGenerator: url,
                      cardTheme,
                      withBorder,
                  })
                : null,
            !withoutVideos
                ? createBrandVideosRow(brand, {
                      urlGenerator: url,
                      withBorder,
                      query,
                  })
                : null,
            !withoutSeries
                ? createBrandSeriesRow(brand, {
                      urlGenerator: url,
                      query,
                  })
                : null,
            ...(additionalBlocks || []),
            ...(!withoutSections
                ? sections
                      .filter(({ hidden = false }) => !hidden)
                      .map(({ id: sectionId, label, slug }) =>
                          createDocumentsGrid(12, {
                              title: (
                                  <RowTitle
                                      url={url('section', { brand: brandSlug, section: slug })}
                                  >
                                      {label}
                                  </RowTitle>
                              ),
                              query: {
                                  brand: brandId,
                                  section: sectionId,
                              },
                              clickRef: `home_section_${slug}`,
                              loading: 'lazy',
                              cardImageLoading: 'lazy',
                              cardTheme,
                              cardWithout: ['category'],
                              uniqueDocuments: true,
                              withBorder,
                          }),
                      )
                : []),
        ].filter((b) => b !== null);

    const uniqueBlocks = useUniqueDocumentsBlocks(finalBlocks as BlockWithUniqueDocuments[]);
    const blocksWithAddons = getBlocksWithAddons(uniqueBlocks, {
        addons: [
            {
                block: 'ad',
                max: null,
                interval: adInterval,
                minHeight: 2,
            },
        ],

        addonsProps: {
            ad: ({ count = 0 }) => ({
                slot: count === 0 ? 'fullwidth_native' : 'fullwidth',
            }),
        },
        minSpaceBetween: 1,
    });

    const [bumperEnded, setBumperEnded] = useState(false);
    const [bumperUnmounted, setBumperUnmounted] = useState(false);
    const [shouldPlayBumper, setShouldPlayBumper] = useState(current);
    useEffect(() => {
        setShouldPlayBumper(current);
    }, [current]);
    const bumperPlaying =
        brandHandle !== null && current && !bumperEnded && !withoutBumper && shouldPlayBumper;

    const onBumperEnd = () => {
        setBumperEnded(true);
    };

    const onBumperFail = () => {
        setBumperEnded(true);
        setBumperUnmounted(true);
    };

    useEffect(() => {
        let timeout = null;
        if (bumperEnded && !bumperPlaying) {
            timeout = setTimeout(() => {
                setBumperUnmounted(true);
            }, 500);
        }
        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [bumperEnded, bumperPlaying]);

    const { height: windowHeight = 0 } = useWindowSize();
    const { ref: containerRef, height: containerHeight = 0 } = useElementSize({
        disabled: !current,
    });

    useScrollEnter({
        leaveThreshold:
            containerHeight > 0 && windowHeight > 0 ? containerHeight - windowHeight : null,
        disabled: !current,
        onEnter,
        onLeave,
    });

    usePageViewTracking(current, {
        brand,
        pageType,
    });

    const targeting = {
        pageType,
    };

    return (
        <AdsTargetingProvider targeting={targeting}>
            <div
                className={classNames([
                    styles.container,
                    {
                        [styles.bumperIsVisible]: bumperPlaying,
                    },
                    styles[brandHandle],
                    className,
                    contentClassName,
                ])}
                ref={containerRef as Ref<HTMLDivElement>}
                data-brand={brandHandle}
                data-theme={theme}
            >
                {!bumperUnmounted ? (
                    <Bump
                        brand={brand}
                        playing={bumperPlaying}
                        onEnd={onBumperEnd}
                        onFail={onBumperFail}
                        className={styles.bumper}
                    />
                ) : null}
                <HeaderAd />
                {/* Headers positioned over the first block stay below the top ad */}
                <div className={styles.content}>
                    {header !== false
                        ? header || (
                              <BrandHeader
                                  {...brand}
                                  withoutSections={withoutHeaderSections}
                                  className={styles.header}
                              />
                          )
                        : null}
                    <Blocks
                        blocks={blocksWithAddons}
                        className={classNames([styles.blocks, blocksClassName])}
                        blockClassName={styles.block}
                    />
                </div>
            </div>
        </AdsTargetingProvider>
    );
};

export default HomePageBlocks;
